import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import authService from './authService';

// API Base URL - Update this with your actual backend URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/plain, */*',
    },
});

// Request interceptor - Add auth token and tenant ID to requests
apiClient.interceptors.request.use(
    async (config: any) => {
        // Get token from localStorage
        let token = localStorage.getItem('authToken');

        // Extend activity timestamp on every API call if we have a token
        if (token) {
            localStorage.setItem('lastActiveTime', String(Date.now()));
        }

        // Get tenant ID from localStorage or rehydrate from JWT claims
        let tenantId = localStorage.getItem('currentTenantId');
        const isUuidPattern = (val: string | null | undefined): boolean =>
            !!val && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val).trim());

        if (!isUuidPattern(tenantId) && token) {
            const tokenFacilityId = authService.getFacilityIdFromToken(token);
            if (tokenFacilityId && isUuidPattern(tokenFacilityId)) {
                tenantId = tokenFacilityId;
                localStorage.setItem('currentTenantId', tokenFacilityId);
            }
        }

        if (token && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Only send X-Tenant-Id if it is a valid UUID, preventing non-UUID strings like 'carex'
        if (tenantId && isUuidPattern(tenantId) && config.headers['X-Tenant-Id'] === undefined) {
            config.headers['X-Tenant-Id'] = tenantId;
        }

        // Deep Guard: Ensure no outgoing request carries non-UUID or 'carex' in params/url
        const activeFacilityUuid = (isUuidPattern(tenantId) ? tenantId : null) || (token ? authService.getFacilityIdFromToken(token) : null);
        if (activeFacilityUuid) {
            if (config.params && typeof config.params === 'object') {
                if (config.params.facilityId && !isUuidPattern(config.params.facilityId)) {
                    config.params.facilityId = activeFacilityUuid;
                }
            }
            if (config.url && typeof config.url === 'string') {
                config.url = config.url
                    .replace(/([?&]facilityId=)([^&]+)/gi, (match: string, prefix: string, val: string) => isUuidPattern(val) ? match : prefix + activeFacilityUuid)
                    .replace(/(\/facilit(?:y|ies)\/)carex(?=[\/?#]|$)/gi, (_match: string, prefix: string) => prefix + activeFacilityUuid);
            }
        }

        return config;
    },
    (error: AxiosError) => {
        return Promise.reject(error);
    }
);

// Track whether a token refresh is already in-flight to avoid multiple concurrent refreshes
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string | null) => void; reject: (error: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
};

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error.response) {
            const status = error.response.status;

            if (status === 401 && !originalRequest._retry) {
                // First 401: try to refresh the token and retry the request
                if (isRefreshing) {
                    // Another refresh is already in-flight; queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({
                            resolve: (token) => {
                                if (token && originalRequest.headers) {
                                    originalRequest.headers.Authorization = `Bearer ${token}`;
                                }
                                resolve(apiClient(originalRequest));
                            },
                            reject,
                        });
                    });
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    const newToken = await authService.refreshToken();
                    if (newToken) {
                        const refreshedFacilityId = authService.getFacilityIdFromToken(newToken);
                        if (refreshedFacilityId) {
                            localStorage.setItem('currentTenantId', refreshedFacilityId);
                        }
                        // Retry the original request with the new token
                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        processQueue(null, newToken);
                        return apiClient(originalRequest);
                    } else {
                        // Refresh returned null — truly unauthenticated
                        processQueue(new Error('Refresh failed'), null);
                        localStorage.removeItem('user');
                        localStorage.removeItem('authToken');
                        localStorage.removeItem('refreshToken');
                        window.location.href = '/login?reason=session_expired';
                    }
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    localStorage.removeItem('user');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('refreshToken');
                    window.location.href = '/login?reason=session_expired';
                } finally {
                    isRefreshing = false;
                }
            } else if (status === 403) {
                // Forbidden
                console.error('Access forbidden');
            } else if (status === 500) {
                // Server error
                console.error('Server error occurred');
            }
        } else if (error.request) {
            // Request made but no response
            console.error('Network error - no response from server');
        } else {
            // Something else happened
            console.error('Error:', error.message);
        }

        return Promise.reject(error);
    }
);

export default apiClient;